import { ref, onMounted, onUnmounted, watch } from "vue";
import type { Ref } from "vue";

/**
 * Composable для отслеживания изменения позиции и размера элемента.
 * Использует ResizeObserver и события прокрутки/изменения размера окна.
 */
export function useElementObserver(
  $element: Ref<HTMLElement | null> | HTMLElement
) {
  // Приведение $element к Ref
  const $elementRef = ref<HTMLElement | null>(
    ($element instanceof HTMLElement ? $element : $element?.value) || null
  );

  // Реактивные значения для позиции элемента
  const position = ref({ top: 0, left: 0, right: 0, bottom: 0 });

  // Реактивные значения для размеров элемента
  const size = ref({ width: 0, height: 0 });

  // Объект ResizeObserver
  let resizeObserver: ResizeObserver | null = null;

  /**
   * Функция для обновления позиции и размера элемента.
   * Вызывает getBoundingClientRect() для получения актуальных данных.
   */
  const updatePositionAndSize = () => {
    if ($elementRef.value) {
      const rect = $elementRef.value.getBoundingClientRect();
      position.value = {
        top: rect.top + window.scrollY, // Добавляем вертикальную прокрутку
        left: rect.left + window.scrollX, // Добавляем горизонтальную прокрутку
        right: window.innerWidth - (rect.left + rect.width + window.scrollX), // Позиция справа с учетом прокрутки
        bottom: window.innerHeight - (rect.top + rect.height + window.scrollY), // Позиция снизу с учетом прокрутки
      };
      size.value = { width: rect.width, height: rect.height };
    }
  };

  /**
   * Инициализация наблюдателей и обработчиков.
   */
  const initObservers = () => {
    if (!$elementRef.value) return;

    // Инициализируем начальные данные
    updatePositionAndSize();

    // Инициализируем ResizeObserver
    resizeObserver = new ResizeObserver(() => updatePositionAndSize());
    resizeObserver.observe($elementRef.value);

    // Добавляем обработчики событий для прокрутки и изменения размера окна
    window.addEventListener("scroll", updatePositionAndSize);
    window.addEventListener("resize", updatePositionAndSize);
  };

  /**
   * Очистка наблюдателей и обработчиков.
   */
  const cleanupObservers = () => {
    resizeObserver?.disconnect();
    resizeObserver = null;
    window.removeEventListener("scroll", updatePositionAndSize);
    window.removeEventListener("resize", updatePositionAndSize);
  };

  /**
   * Логика, выполняемая при монтировании компонента.
   */
  onMounted(() => {
    if ($element instanceof HTMLElement) {
      $elementRef.value = $element;
    }

    // Наблюдаем за изменением $elementRef, если он передан как Ref
    if (!($element instanceof HTMLElement)) {
      watch(
        () => $element.value,
        (newEl) => {
          cleanupObservers(); // Очищаем предыдущие обработчики
          $elementRef.value = newEl;
          if (newEl) initObservers(); // Повторная инициализация
        },
        { immediate: true }
      );
    } else {
      initObservers();
    }
  });

  /**
   * Логика, выполняемая при размонтировании компонента.
   */
  onUnmounted(() => {
    cleanupObservers();
  });

  return {
    /** Позиция элемента `{ top, left, right, bottom }`. */
    position,
    /** Размеры элемента `{ width, height }`. */
    size,
    /**
     * Функция для обновления позиции и размера элемента.
     * Вызывает getBoundingClientRect() для получения актуальных данных.
     */
    updatePositionAndSize,
  };
}
